import HcrDetailsBox from "components/common/HcrDetailsBox";
import React, { useEffect, useState } from "react";
import BillsCard from "./components/billsCard";
import { useAppDispatch, useAppSelector } from "utils/hooks";
import { useNavigate, useParams } from "react-router-dom";
import { currentClaimFormDataThunk, lumpSumListThunk, updateFormV2Thunk, updateFormV2ThunkForSave } from "store/thunk/hcr.thunk";
import { useFormik } from "formik";
import { toast } from "react-toastify";
import LumpSumCard from "./components/lumpSumCard";
import BillSum from "./components/modals/BillSum";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
dayjs.extend(customParseFormat);

const dateConvertorForApi = (bills: any) => {
  if (Array.isArray(bills) && bills?.length > 0) {
    const temp = bills.map((obj) => ({ ...obj, date: dayjs(obj.date, "YYYY-MM-DD").format("DD/MM/YYYY") }));
    return temp;
  }
  return [];
};
const dateConvertorFromApi = (bills: any) => {
  if (Array.isArray(bills) && bills?.length > 0) {
    const temp = bills.map((obj) => ({ ...obj, date: dayjs(obj.date, "DD/MM/YYYY").format("YYYY-MM-DD") }));
    return temp;
  }
  return [];
};

const ClaimBillsScreen = () => {
  const hcrId: any = useParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [showBillSumModal, setShowBillSumModal] = useState(false);

  const hcrDetails = useAppSelector((state) => state.rootReducer.hcrSlice.currentClaimForm);
  const lumpSumList = useAppSelector((state) => state.rootReducer.hcrSlice.lumpSumList);

  useEffect(() => {
    dispatch(currentClaimFormDataThunk(hcrId.id));
    dispatch(lumpSumListThunk());
  }, [dispatch, hcrId.id]);

  const { values, setFieldValue, handleSubmit }: any = useFormik({
    enableReinitialize: true,
    initialValues: {
      hospitalMainBill: hcrDetails?.hospitalMainBill?.length === 0 ? [{ issuedBy: "", amount: "", date: "", billNumber: "" }] : dateConvertorFromApi(hcrDetails?.hospitalMainBill),
      pharmacyBills: dateConvertorFromApi(hcrDetails?.pharmacyBills) || [],
      preHospitalBill: dateConvertorFromApi(hcrDetails?.preHospitalBill) || [],
      postHospitalBill: dateConvertorFromApi(hcrDetails?.postHospitalBill) || [],
      otherExpense: dateConvertorFromApi(hcrDetails?.otherExpense) || [],
      lumpSumCashBenefit: hcrDetails?.lumpSumCashBenefit?.length === 0 ? lumpSumList : hcrDetails?.lumpSumCashBenefit
    },
    onSubmit: (values) => {
      if (!validationChecker()) return;
      else if (!validateLumpSumCashBenefit()) return;
      if (!showBillSumModal) {
        setShowBillSumModal(true);
        return;
      }
      const payload = {
        ...values,
        _id: hcrId.id,
        hospitalMainBill: dateConvertorForApi(values.hospitalMainBill),
        pharmacyBills: dateConvertorForApi(values.pharmacyBills),
        preHospitalBill: dateConvertorForApi(values.preHospitalBill),
        postHospitalBill: dateConvertorForApi(values.postHospitalBill),
        otherExpense: dateConvertorForApi(values.otherExpense),
        step: 7
      };
      dispatch(updateFormV2Thunk(payload))
        .unwrap()
        .then((data) => {
          setShowBillSumModal(false);
          if (data?.success) navigate("/hcr/bank-details/" + hcrId?.id);
        });
    }
  });

  const saveProgressHandler = () => {
    const payload = {
      ...values,
      _id: hcrId.id,
      hospitalMainBill: dateConvertorForApi(values.hospitalMainBill),
      pharmacyBills: dateConvertorForApi(values.pharmacyBills),
      preHospitalBill: dateConvertorForApi(values.preHospitalBill),
      postHospitalBill: dateConvertorForApi(values.postHospitalBill),
      otherExpense: dateConvertorForApi(values.otherExpense),
      step: 7
    };
    dispatch(updateFormV2ThunkForSave(payload))
      .unwrap()
      .then((data) => {
        if (data?.success) {
          toast.success("Details saved successfully");
        }
      });
  };

  const validationChecker = () => {
    let isValid = true;

    Object.entries(values).forEach(([key, array]) => {
      if (key === "lumpSumCashBenefit") return;

      if (Array.isArray(array)) {
        array.forEach((obj) => {
          // eslint-disable-next-line
          const emptyEntry = Object.entries(obj).find(([_, value]) => [null, undefined, ""].includes(value as string | null | undefined));

          if (emptyEntry && isValid) {
            toast.error(`Please fill ${emptyEntry[0].toUpperCase()} in ${key.toUpperCase()}`);
            isValid = false;
          }
        });
      }
    });

    return isValid;
  };

  const validateLumpSumCashBenefit = () => {
    // eslint-disable-next-line
    for (const [index, obj] of values.lumpSumCashBenefit.entries()) {
      if (obj.isSelected) {
        if ([null, undefined, ""].includes(obj.amount)) {
          toast.error(`Please Fill Amount in ${obj.title}`);
          return false;
        }
        if (obj.title === "Others" && [null, undefined, ""].includes(obj.description.trim())) {
          toast.error(`Please Enter Description in Others`);
          return false;
        }
      }
    }

    return true;
  };

  return (
    <>
      <HcrDetailsBox progress={60} title="Details Of Claim/Bills" nextAction={handleSubmit} saveProgress={saveProgressHandler}>
        <BillsCard title="Hospital Main Bill" id="hospitalMainBill" values={values} setFieldValue={setFieldValue} />
        <BillsCard title="Pharmacy Bill" id="pharmacyBills" values={values} setFieldValue={setFieldValue} />
        <BillsCard title="Pre Hospital Bill" id="preHospitalBill" values={values} setFieldValue={setFieldValue} />
        <BillsCard title="Post Hospital Bill" id="postHospitalBill" values={values} setFieldValue={setFieldValue} />
        <BillsCard title="Other Expenses" id="otherExpense" values={values} setFieldValue={setFieldValue} />
        <LumpSumCard values={values} setFieldValue={setFieldValue} />
      </HcrDetailsBox>
      <BillSum showModal={showBillSumModal} onClose={() => setShowBillSumModal(!showBillSumModal)} billDetails={values} submitHandler={handleSubmit} />
    </>
  );
};

export default ClaimBillsScreen;
